import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, InputLabel, LinearProgress, Link,
  MenuItem, Paper, Select, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import arrowBackFill from '@iconify/icons-eva/arrow-back-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import eyeFill from '@iconify/icons-eva/eye-fill';
import Page from '../Page';
import HtmlEditor from '../Outreach/shared/HtmlEditor';
import {
  getMyReviewQueue, approveQueueEntry, rejectQueueEntry, updateQueueEntryMessages,
  listInvalidReasons, markRawCompanyInvalid,
} from '../../utils/outreachApi';

const MSG_DEFS = [
  { key: 'email1',          label: 'Step 1 — Cold intro',     hasSubject: true,  channel: 'email' },
  { key: 'linkedin_invite', label: 'Step 2 — LinkedIn note',  hasSubject: false, channel: 'linkedin' },
  { key: 'email_followup',  label: 'Step 3 — Follow-up',      hasSubject: true,  channel: 'email' },
];

// Mirrors backend `_compose_email_body` logic so the BA previews exactly what
// the recipient will see: if the body contains tags it renders as HTML;
// otherwise newlines become <p>/<br> structure.
const HTML_TAG_RE = /<\s*[a-zA-Z!/][^>]*>/;

const escapeHtml = (s) => (s || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const bodyToPreviewHtml = (body) => {
  if (!body) return '';
  if (HTML_TAG_RE.test(body)) return body;
  return body.split('\n\n').filter((p) => p.trim()).map((p) =>
    `<p style="margin:0 0 12px 0">${escapeHtml(p).replace(/\n/g, '<br>')}</p>`
  ).join('');
};

// Format an integer revenue (e.g. 12_500_000 → "$12.5M") for compact display.
const fmtRevenue = (n) => {
  if (n == null) return null;
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000)     return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)         return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
};

const stripProto = (url) => (url || '').replace(/^https?:\/\//, '').replace(/\/$/, '');

// Tight key/value row used by the side panel.
const InfoRow = ({ label, value, href }) => {
  if (value == null || value === '') return null;
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ py: 0.4 }}>
      <Typography variant="caption"
                  sx={{ minWidth: 74, color: 'text.secondary', textTransform: 'uppercase',
                        fontSize: 10, fontWeight: 700, letterSpacing: 0.55, lineHeight: 1.65 }}>
        {label}
      </Typography>
      {href ? (
        <Link href={href} target="_blank" rel="noopener noreferrer"
              underline="hover" variant="body2"
              sx={{ wordBreak: 'break-all' }}>
          {value}
        </Link>
      ) : (
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
      )}
    </Stack>
  );
};

// Side panel rendered in the right column of the review dialog. Pulls every
// useful field off the queue entry so the BA can review with context visible.
function CompanyContextPanel({ entry }) {
  if (!entry) return null;

  const apollo = (entry.li_contact_data || {}).apollo_record || {};
  const org    = apollo.organization || {};

  const contactLi = apollo.linkedin_url
                  || (entry.li_contact_data || {}).linkedin_url
                  || '';

  // Resolve each company fact through a fallback chain:
  //   Company row → Apollo organization record → Perplexity research → "N/A".
  // Apollo often has revenue/founded/industry the Company row is missing; the
  // qualify-mode Perplexity payload (template flow) fills the rest. We show
  // "N/A" for core facts rather than a misleading "$0".
  const perplexity = entry.perplexity_data || {};
  const px = perplexity.mode === 'qualify' ? perplexity : {};   // structured qualify fields
  const num = (v) => (v != null && Number(v) > 0 ? Number(v) : null);

  // Perplexity sometimes returns placeholder filler ("unknown", "not verifiable
  // from available sources", etc.) instead of a real finding. Treat all of that
  // as empty so it never renders. Also strips filler items out of lists.
  const FILLER = /^\s*(unknown|n\/?a|none|not\s+(verifiable|available|found|specified|known|public)|no\s+(information|data|public|specific)|insufficient|undisclosed|tbd)\b/i;
  const clean = (v) => {
    const s = (v == null ? '' : String(v)).trim();
    return (!s || FILLER.test(s)) ? '' : s;
  };
  const cleanList = (arr) => (Array.isArray(arr) ? arr.map((x) => clean(x)).filter(Boolean) : []);

  const withProto = (u) => (u ? (u.startsWith('http') ? u : `https://${u}`) : null);
  const industry  = entry.company_industry || org.industry || 'N/A';
  const employeesNum = entry.company_employees != null ? entry.company_employees
                     : (org.estimated_num_employees != null ? org.estimated_num_employees
                     : px.employee_estimate);
  const employees = employeesNum != null ? Number(employeesNum).toLocaleString() : 'N/A';
  // Revenue: a real number from Company/Apollo → else Perplexity's text estimate → N/A.
  const revenueNum = num(entry.company_revenue) || num(org.organization_revenue);
  const revenueDisplay = revenueNum ? fmtRevenue(revenueNum)
                       : (clean(px.revenue_estimate) || 'N/A');
  const founded   = entry.company_foundation_year || org.founded_year || px.founded_year || 'N/A';
  const website   = entry.company_website || org.website_url || '';
  const websiteHref = withProto(website);
  const companyLi = org.linkedin_url || '';
  const crunchbase = org.crunchbase_url || '';
  const keywords  = Array.isArray(org.keywords) ? org.keywords.filter(Boolean).slice(0, 8).join(' · ') : '';
  // Funding: Apollo printed total → else Perplexity funding stage. Hidden when absent.
  const fundingStage = clean(px.funding_stage) ? String(px.funding_stage).replace(/_/g, ' ') : '';
  const funding = org.total_funding_printed
    || (org.total_funding != null ? fmtRevenue(org.total_funding) : '')
    || fundingStage
    || '';
  const lastRound = org.latest_funding_stage || '';
  const growth12  = org.organization_headcount_twelve_month_growth;
  const growthPct = (num(growth12) != null)
    ? `+${Math.round(Number(growth12) * 100)}% (12mo headcount)`
    : null;
  // Founder involvement, SaaS and independence are determined deterministically
  // elsewhere (Apollo + LinkedIn employment check, SaaS classifier,
  // independence_check) — we do NOT surface Perplexity's guesses on them.
  const signals = cleanList(px.notable_signals);
  const pains   = cleanList(px.potential_pain_points);
  const fundingHistory = clean(px.funding_history);

  const liActivity = entry.li_activity_data || {};
  const recentPosts = Array.isArray(liActivity.posts) ? liActivity.posts
                    : Array.isArray(liActivity.recent_posts) ? liActivity.recent_posts
                    : [];
  const perplexitySummary = perplexity.summary || perplexity.research_summary || perplexity.text || '';
  const cleanSummary = clean(perplexitySummary);
  const hasResearch = cleanSummary || fundingHistory || signals.length > 0 || pains.length > 0;

  return (
    <Stack spacing={2}>
      {/* AI analysis — show first since it most directly informs the approve/reject call */}
      {(entry.personalization_hook || entry.primary_gap || entry.scoring_reason) && (
        <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 1.5, bgcolor: 'background.neutral' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
            AI ANALYSIS
          </Typography>
          {entry.personalization_hook && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Personalization hook</Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.25 }}>"{entry.personalization_hook}"</Typography>
            </Box>
          )}
          {entry.primary_gap && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Primary gap</Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>{entry.primary_gap}</Typography>
            </Box>
          )}
          {entry.scoring_reason && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Scoring reason</Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>{entry.scoring_reason}</Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Company */}
      <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 1.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
          COMPANY
        </Typography>
        <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
          <InfoRow label="Name"        value={entry.company_name} />
          <InfoRow label="Industry"    value={industry} />
          <InfoRow label="Country"     value={entry.company_country || 'N/A'} />
          <InfoRow label="Employees"   value={employees} />
          <InfoRow label="Revenue"     value={revenueDisplay} />
          <InfoRow label="Founded"     value={founded} />
          <InfoRow label="Growth"      value={growthPct} />
          <InfoRow label="Funding"     value={funding || null} />
          <InfoRow label="Last round"  value={lastRound || null} />
          <InfoRow label="Website"     value={stripProto(website)} href={websiteHref} />
          <InfoRow label="LinkedIn"    value={stripProto(companyLi)} href={withProto(companyLi)} />
          <InfoRow label="Crunchbase"  value={crunchbase ? 'View profile' : null} href={crunchbase || null} />
          <InfoRow label="Keywords"    value={keywords || null} />
        </Stack>
        {entry.company_business_description && (
          <Box sx={{ mt: 1.25, pt: 1.25, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase',
                                                fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>
              About
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.25, color: 'text.secondary', lineHeight: 1.5 }}>
              {entry.company_business_description}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Contact */}
      <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>CONTACT</Typography>
          <VerificationChip entry={entry} />
        </Stack>
        <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
          <InfoRow label="Name"     value={entry.contact_name} />
          <InfoRow label="Title"    value={entry.contact_title} />
          <InfoRow label="Email"    value={entry.contact_email} href={entry.contact_email ? `mailto:${entry.contact_email}` : null} />
          <InfoRow label="LinkedIn" value={stripProto(contactLi)} href={contactLi || null} />
        </Stack>
      </Paper>

      {/* Recent LinkedIn posts */}
      {recentPosts.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
            RECENT LINKEDIN POSTS
          </Typography>
          <Stack spacing={1}>
            {recentPosts.slice(0, 3).map((p, i) => {
              const text = p.text || p.content || p.body || '';
              const url  = p.url || p.post_url;
              const when = p.posted_at || p.published_at || p.date || '';
              return (
                <Box key={i}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                    {when}
                  </Typography>
                  <Typography variant="body2" sx={{
                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden', mb: 0.25, lineHeight: 1.45,
                  }}>
                    {text}
                  </Typography>
                  {url && (
                    <Link href={url} target="_blank" rel="noopener noreferrer"
                          variant="caption" underline="hover">
                      Open post
                    </Link>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* Web research (Perplexity) — qualify-mode facts + free-text summary */}
      {hasResearch && (
        <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
            WEB RESEARCH
          </Typography>
          <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            <InfoRow label="Funding"  value={fundingHistory || null} />
          </Stack>
          {signals.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Signals</Typography>
              <Box component="ul" sx={{ m: 0.25, pl: 2.25 }}>
                {signals.slice(0, 5).map((s, i) => (
                  <Typography key={i} component="li" variant="body2" sx={{ lineHeight: 1.45 }}>{s}</Typography>
                ))}
              </Box>
            </Box>
          )}
          {pains.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Potential gaps</Typography>
              <Box component="ul" sx={{ m: 0.25, pl: 2.25 }}>
                {pains.slice(0, 5).map((s, i) => (
                  <Typography key={i} component="li" variant="body2" sx={{ lineHeight: 1.45 }}>{s}</Typography>
                ))}
              </Box>
            </Box>
          )}
          {cleanSummary && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', lineHeight: 1.5,
                                              whiteSpace: 'pre-wrap',
                                              display: '-webkit-box', WebkitLineClamp: 8, WebkitBoxOrient: 'vertical',
                                              overflow: 'hidden' }}>
              {cleanSummary}
            </Typography>
          )}
        </Paper>
      )}
    </Stack>
  );
}

function VerificationChip({ entry }) {
  const v = entry.employment_verification;
  if (!v) return null;
  const meta = {
    CURRENT:              { label: '✓ Verified',           color: 'success' },
    POSSIBLY_LEFT:        { label: '⚠ Employer mismatch',  color: 'error'   },
    UNVERIFIED:           { label: 'Unverified',           color: 'default' },
    FETCH_FAILED:         { label: 'Fetch failed',         color: 'default' },
    NO_LINKEDIN_URL:      { label: 'No LinkedIn',          color: 'default' },
    NO_LINKEDIN_EMAIL_OK: { label: 'Email-only',           color: 'info'    },
    FETCH_FAILED_EMAIL_OK:{ label: 'Email-only (LI fail)', color: 'info'    },
  }[v.status];
  if (!meta) return null;
  return <Chip label={meta.label} size="small" color={meta.color}
    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />;
}

// ─── Single entry's review modal ─────────────────────────────────────────────

function ReviewDialog({ entry, siblingEntries = [], open, onClose, onActed, onSnack }) {
  const [msgs, setMsgs] = useState({});
  const [msgsDirty, setMsgsDirty] = useState(false);
  const [notes, setNotes] = useState('');
  const [acting, setActing] = useState(false);
  // Per-message-key view mode: 'preview' (default — render as recipient sees)
  // or 'edit' (raw textarea so the BA can tweak markup).
  const [previewMode, setPreviewMode] = useState({});

  // Reject-prompt sub-dialog state.
  const [rejectOpen, setRejectOpen]                 = useState(false);
  const [rejectAll, setRejectAll]                   = useState(false);
  const [invalidReasons, setInvalidReasons]         = useState([]);
  const [reasonsLoading, setReasonsLoading]         = useState(false);
  const [selectedReasonId, setSelectedReasonId]     = useState('');
  const [invalidDescription, setInvalidDescription] = useState('');

  useEffect(() => {
    if (!entry) return;
    setMsgs(entry.generated_messages || {});
    setNotes(entry.reviewer_notes || '');
    setMsgsDirty(false);
    // Reset reject-prompt state on every new entry.
    setRejectOpen(false);
    setRejectAll(false);
    setSelectedReasonId('');
    setInvalidDescription('');
    // Reset every message to Preview by default whenever a new entry opens.
    setPreviewMode({
      email1: 'preview', linkedin_invite: 'preview', email_followup: 'preview',
    });
  }, [entry?.id]);

  if (!entry) return null;

  const onMsgChange = (key, field, value) => {
    setMsgs((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
    setMsgsDirty(true);
  };

  const saveMessagesIfDirty = async () => {
    if (msgsDirty) await updateQueueEntryMessages(entry.id, msgs);
    setMsgsDirty(false);
  };

  const handleApprove = async () => {
    setActing(true);
    try {
      await saveMessagesIfDirty();
      await approveQueueEntry(entry.id, { reviewer_notes: notes });
      onSnack({ severity: 'success', message: `Approved — ${entry.company_name} will be enrolled.` });
      onActed(entry.id);
    } catch (e) {
      onSnack({ severity: 'error', message: e?.response?.data?.detail || 'Approve failed.' });
    } finally { setActing(false); }
  };

  // Reject button now ALWAYS opens a confirmation prompt — even single-contact
  // rejections get the prompt so the BA can optionally mark the whole company
  // invalid (per the requirements).
  const openRejectPrompt = async () => {
    setRejectOpen(true);
    // Lazy-load reasons the first time the prompt is opened.
    if (invalidReasons.length === 0 && !reasonsLoading) {
      setReasonsLoading(true);
      try {
        const r = await listInvalidReasons();
        const data = r?.data;
        const list = data?.data || (Array.isArray(data) ? data : []);
        setInvalidReasons(list);
      } catch (e) {
        // Reasons can still be loaded later — just disable the "mark invalid"
        // option if the call fails.
        setInvalidReasons([]);
      } finally {
        setReasonsLoading(false);
      }
    }
  };

  const confirmReject = async () => {
    if (rejectAll && !selectedReasonId) {
      onSnack({ severity: 'error', message: 'Pick an invalid reason to mark the company invalid.' });
      return;
    }
    setActing(true);
    try {
      // 1. Reject the current contact.
      await rejectQueueEntry(entry.id, { reviewer_notes: notes });

      // 2. Optionally cascade-reject every other contact of this company in this campaign.
      const cascadeIds = [];
      if (rejectAll && siblingEntries.length > 0) {
        for (const sib of siblingEntries) {
          try {
            await rejectQueueEntry(sib.id, { reviewer_notes: 'Cascade: company marked invalid' });
            cascadeIds.push(sib.id);
          } catch (e) {
            // Best-effort — keep going even if one sibling fails.
          }
        }
      }

      // 3. Optionally mark the parent Company as invalid (moves it to 5% stage).
      if (rejectAll) {
        try {
          await markRawCompanyInvalid(entry.company, {
            reason_id:   selectedReasonId,
            description: invalidDescription.trim() || `Outreach review (${entry.company_name})`,
            campaign_id: entry.campaign,
          });
        } catch (e) {
          onSnack({
            severity: 'warning',
            message: 'Contacts rejected, but marking the company invalid failed. Please retry from the dashboard.',
          });
        }
      }

      const msg = rejectAll
        ? `${entry.company_name} marked invalid · ${cascadeIds.length + 1} contact${cascadeIds.length === 0 ? '' : 's'} rejected.`
        : `Rejected — ${entry.contact_name || entry.company_name}.`;
      onSnack({ severity: 'info', message: msg });
      setRejectOpen(false);
      onActed([entry.id, ...cascadeIds]);
    } catch (e) {
      onSnack({ severity: 'error', message: e?.response?.data?.detail || 'Reject failed.' });
    } finally {
      setActing(false);
    }
  };

  const handleSaveOnly = async () => {
    // Persist message edits (and notes) to this queue entry without approving.
    // BA can come back later to approve or reject — keeps editing/approval decoupled.
    if (!msgsDirty && !notes) {
      onSnack({ severity: 'info', message: 'Nothing to save.' });
      return;
    }
    setActing(true);
    try {
      await saveMessagesIfDirty();
      // Persist notes too (handled atomically with approve normally, but here
      // we want notes saved without changing status). We piggyback via the
      // messages-update endpoint by storing the notes inside generated_messages.
      // (If a dedicated notes endpoint exists later, swap to that.)
      onSnack({ severity: 'success', message: `Changes saved — ${entry.company_name}. Entry still in review.` });
    } catch (e) {
      onSnack({ severity: 'error', message: e?.response?.data?.detail || 'Save failed.' });
    } finally { setActing(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
            PaperProps={{ sx: { height: '92vh', maxHeight: '92vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{entry.company_name}</Typography>
          <VerificationChip entry={entry} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {entry.contact_name || 'Contact'}{entry.contact_title ? ` · ${entry.contact_title}` : ''}
          {entry.contact_email ? ` · ${entry.contact_email}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', minHeight: 0 }}>
        {/* Left column — messages + notes (scrolls independently) */}
        <Box sx={{ flex: 1.4, minWidth: 0, overflowY: 'auto', p: 2.5,
                   borderRight: 1, borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, display: 'block', mb: 1.25 }}>
            MESSAGES TO BE SENT
          </Typography>
          <Stack spacing={1.5}>
            {MSG_DEFS.map(({ key, label, hasSubject }) => {
              const m = msgs[key] || {};
              const mode = previewMode[key] || 'preview';
              const setMode = (next) => setPreviewMode((p) => ({ ...p, [key]: next }));
              return (
                <Paper key={key} variant="outlined" sx={{ p: 1.75, borderRadius: 1.5 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                      {label}
                    </Typography>
                    <Stack direction="row" spacing={0}>
                      <Button
                        size="small"
                        variant={mode === 'preview' ? 'contained' : 'text'}
                        color={mode === 'preview' ? 'primary' : 'inherit'}
                        onClick={() => setMode('preview')}
                        sx={{ minWidth: 0, px: 1.25, py: 0.25, fontSize: 11, textTransform: 'none' }}
                      >
                        Preview
                      </Button>
                      <Button
                        size="small"
                        variant={mode === 'edit' ? 'contained' : 'text'}
                        color={mode === 'edit' ? 'primary' : 'inherit'}
                        onClick={() => setMode('edit')}
                        sx={{ minWidth: 0, px: 1.25, py: 0.25, fontSize: 11, textTransform: 'none' }}
                      >
                        Edit source
                      </Button>
                    </Stack>
                  </Stack>

                  {hasSubject && (
                    <TextField size="small" fullWidth label="Subject" value={m.subject || ''}
                      onChange={(e) => onMsgChange(key, 'subject', e.target.value)} sx={{ mb: 1.25 }} />
                  )}

                  {mode === 'preview' ? (
                    <Box sx={{
                      px: 1.75, py: 1.5, borderRadius: 1,
                      border: '1px solid', borderColor: 'divider',
                      bgcolor: 'background.paper',
                      minHeight: 96,
                      fontSize: 14, lineHeight: 1.55,
                      '& a':  { color: 'primary.main' },
                      '& p':  { margin: '0 0 12px 0' },
                      '& p:last-child': { marginBottom: 0 },
                      '& ul, & ol': { paddingLeft: 20, margin: '0 0 12px 0' },
                      '& img': { maxWidth: '100%' },
                    }}>
                      {(m.body || '').trim() ? (
                        <div dangerouslySetInnerHTML={{ __html: bodyToPreviewHtml(m.body) }} />
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          (empty)
                        </Typography>
                      )}
                      {/* Signature — only on email steps; rendered exactly as the
                          recipient will see it. Comes from the assigned BA's name
                          through the same render_tgc_signature path used at send time. */}
                      {key.startsWith('email') && entry.pm_signature_html && (
                        <Box sx={{ mt: 2.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                          <div dangerouslySetInnerHTML={{ __html: entry.pm_signature_html }} />
                        </Box>
                      )}
                      {key.startsWith('email') && !entry.pm_signature_html && (
                        <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mt: 2, pt: 1.25, borderTop: 1, borderColor: 'divider',
                                fontStyle: 'italic' }}>
                          + your TGC signature appended automatically at send
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <HtmlEditor
                      value={m.body || ''}
                      onChange={(html) => onMsgChange(key, 'body', html)}
                      placeholder="Edit the message body…"
                      minHeight={key === 'linkedin_invite' ? 140 : 200}
                      compact={key === 'linkedin_invite'}
                    />
                  )}
                </Paper>
              );
            })}
          </Stack>

          <TextField fullWidth multiline minRows={2} size="small" sx={{ mt: 2 }}
            label="Review notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Box>

        {/* Right column — company context (scrolls independently) */}
        <Box sx={{ width: 380, flexShrink: 0, overflowY: 'auto', p: 2.5,
                   bgcolor: 'background.neutral' }}>
          <CompanyContextPanel entry={entry} />
        </Box>
      </DialogContent>
      <Box
        sx={{
          px: 3, pt: 1.25, pb: 0.5,
          borderTop: '1px solid', borderColor: 'divider',
          bgcolor: 'warning.lighter',
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: 'warning.dark', fontWeight: 600, display: 'block', lineHeight: 1.35 }}
        >
          ⚠ Approving will enroll this contact into the campaign — the first email goes out as
          soon as the schedule window opens, and the LinkedIn connection + follow-up email
          fire on their respective delays. Make sure the body looks right before clicking.
        </Typography>
      </Box>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={acting}>Close</Button>
        {msgsDirty && (
          <Typography variant="caption" color="warning.dark" sx={{ ml: 1, fontWeight: 600 }}>
            Unsaved changes
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" disabled={acting || !msgsDirty}
          onClick={handleSaveOnly}>
          Save changes
        </Button>
        <Button variant="outlined" color="error" disabled={acting}
          startIcon={<Icon icon={closeCircleFill} />}
          onClick={openRejectPrompt}>
          Reject
        </Button>
        <Button variant="contained" color="success" disabled={acting}
          startIcon={acting ? <CircularProgress size={14} /> : <Icon icon={checkmarkCircle2Fill} />}
          onClick={handleApprove}>
          Approve {msgsDirty && '& Save'}
        </Button>
      </DialogActions>

      {/* Reject confirmation prompt — always shown, even for single-contact reject. */}
      <Dialog
        open={rejectOpen}
        onClose={() => (acting ? null : setRejectOpen(false))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject contact?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2">
              You're about to reject <strong>{entry.contact_name || 'this contact'}</strong>
              {entry.contact_title ? ` (${entry.contact_title})` : ''} at <strong>{entry.company_name}</strong>.
            </Typography>

            {siblingEntries.length > 0 && !rejectAll && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                {siblingEntries.length} other contact{siblingEntries.length === 1 ? '' : 's'} at
                {' '}{entry.company_name} {siblingEntries.length === 1 ? 'is' : 'are'} also in this queue.
              </Alert>
            )}

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Checkbox
                size="small"
                checked={rejectAll}
                onChange={(e) => setRejectAll(e.target.checked)}
                sx={{ p: 0.5, mt: -0.5 }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Reject all contacts and mark <em>{entry.company_name}</em> invalid
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Rejects every queue entry for this company in this campaign and moves the
                  company to the 5% stage with the reason you pick below. This is the same
                  flow used on the /companies dashboard.
                </Typography>
              </Box>
            </Stack>

            {rejectAll && (
              <Stack spacing={1.5}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="invalid-reason-label">Invalid reason</InputLabel>
                  <Select
                    labelId="invalid-reason-label"
                    label="Invalid reason"
                    value={selectedReasonId}
                    onChange={(e) => setSelectedReasonId(e.target.value)}
                    disabled={reasonsLoading}
                  >
                    {reasonsLoading && <MenuItem value=""><em>Loading…</em></MenuItem>}
                    {!reasonsLoading && invalidReasons.length === 0 && (
                      <MenuItem value=""><em>No reasons configured.</em></MenuItem>
                    )}
                    {invalidReasons.map((r) => (
                      <MenuItem key={r.id} value={r.id}>{r.label || r.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Description (optional)"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={invalidDescription}
                  onChange={(e) => setInvalidDescription(e.target.value)}
                  placeholder="What told you this company isn't a fit?"
                />
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setRejectOpen(false)} disabled={acting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmReject}
            disabled={acting || (rejectAll && !selectedReasonId)}
            startIcon={acting ? <CircularProgress size={14} color="inherit" /> : <Icon icon={closeCircleFill} />}
          >
            {rejectAll
              ? `Reject ${siblingEntries.length + 1} & mark invalid`
              : 'Reject contact'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

const MyReviewQueue = () => {
  const navigate = useNavigate();
  // Admin oversight mode: /my-outreach/review?campaign_id=<id>&all_pms=1
  // shows EVERY BA's REVIEW entries for one campaign (each tagged with its BA).
  const [searchParams] = useSearchParams();
  const adminCampaignId = searchParams.get('campaign_id');
  const adminAllPms     = searchParams.get('all_pms') === '1';
  const [sections, setSections] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [snack, setSnack]       = useState(null);
  const [previewEntry, setPreviewEntry] = useState(null);
  // Set of campaign IDs whose section is expanded. Default: only the first one open.
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  // Set of "campaignId:companyId" keys whose company-group accordion is open.
  // Auto-expands single-contact companies; multi-contact ones stay collapsed
  // until the BA clicks the header.
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());
  // IDs ticked across all campaign sections. Cleared after any bulk action.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkActing, setBulkActing] = useState(false);

  const toggleSectionExpanded = (campaignId) => {
    setExpandedCampaigns((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) next.delete(campaignId); else next.add(campaignId);
      return next;
    });
  };

  const toggleCompanyExpanded = (groupKey) => {
    setExpandedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey); else next.add(groupKey);
      return next;
    });
  };

  // Compute company-grouped contacts for a section. Returns array of
  //   { key, companyId, companyName, contacts: [entry, …] }
  // Stable ordering: first-seen company first, original contact order preserved.
  const groupByCompany = (results) => {
    const byKey = new Map();
    const groups = [];
    for (const e of results) {
      const key = e.company != null ? `c:${e.company}` : `n:${e.company_name || ''}`;
      if (!byKey.has(key)) {
        const g = { key, companyId: e.company, companyName: e.company_name, contacts: [] };
        byKey.set(key, g);
        groups.push(g);
      }
      byKey.get(key).contacts.push(e);
    }
    return groups;
  };

  // When sections load, default-expand the first one (only if user hasn't touched anything yet)
  useEffect(() => {
    if (sections.length === 0) return;
    setExpandedCampaigns((prev) => {
      if (prev.size > 0) return prev;        // user already toggled — respect that
      if (sections.length === 1) return new Set([sections[0].campaign_id]);
      return new Set([sections[0].campaign_id]);
    });
  }, [sections.length]);

  const load = useCallback(() => {
    setLoading(true);
    const req = adminCampaignId
      ? getMyReviewQueue(adminCampaignId, { allPms: adminAllPms })
      : getMyReviewQueue();
    req
      .then((r) => {
        const d = r.data || {};
        if (adminCampaignId) {
          const results = d.results || [];
          if (adminAllPms) {
            // One section PER BA, so the admin sees which BA has to review which
            // companies (today + the rest of their pending queue). Reuses the
            // existing per-section grouped rendering (BA name as the header).
            // One section per BA (outer dropdown). Inside each, the body splits
            // into Today / Future sub-dropdowns (see section render). Each entry
            // carries is_today_review to drive that split.
            // Today/Future split, computed client-side from the cap + per-BA
            // order so it's robust even if the API doesn't tag is_today_review.
            const reviewCap = d.cap || 0;
            const seenPerPm = {};
            const byPm = new Map();
            for (const e of results) {
              const pmId = e.assigned_pm || 0;
              const idx = seenPerPm[pmId] || 0;
              seenPerPm[pmId] = idx + 1;
              e.is_today_review = reviewCap ? (idx < reviewCap) : true;
              if (!byPm.has(pmId)) {
                byPm.set(pmId, {
                  campaign_id:   pmId || -1,
                  campaign_name: e.assigned_pm_name || 'Unassigned',
                  is_pm_section: true,
                  results: [], total_pending: 0,
                });
              }
              const s = byPm.get(pmId);
              s.results.push(e);
              s.total_pending += 1;
            }
            setSections(
              Array.from(byPm.values())
                .sort((a, b) => (a.campaign_name || '').localeCompare(b.campaign_name || ''))
            );
          } else {
            setSections(results.length
              ? [{ campaign_id: Number(adminCampaignId), campaign_name: d.campaign_name,
                    results, total_pending: d.total_pending }]
              : []);
          }
        } else {
          setSections(d.sections || []);
        }
      })
      .catch(() => setSections([]))
      .finally(() => setLoading(false));
  }, [adminCampaignId, adminAllPms]);

  useEffect(() => { load(); }, [load]);

  const handleActed = (entryIdOrIds) => {
    // Optimistically drop the entry/entries from the visible list.
    // Accepts a single id (existing call sites) or an array (cascade reject).
    const ids = Array.isArray(entryIdOrIds) ? entryIdOrIds : [entryIdOrIds];
    const removed = new Set(ids);
    setSections((prev) =>
      prev
        .map((s) => ({
          ...s,
          results: s.results.filter((e) => !removed.has(e.id)),
          total_pending: Math.max(0, s.total_pending - ids.length),
        }))
        .filter((s) => s.results.length > 0 || s.total_pending > 0)
    );
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      ids.forEach((id) => { if (next.delete(id)) changed = true; });
      return changed ? next : prev;
    });
    setPreviewEntry(null);
  };

  const toggleSelected = (entryId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId); else next.add(entryId);
      return next;
    });
  };

  const toggleSectionSelectAll = (section) => {
    const ids = section.results.map((e) => e.id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) ids.forEach((id) => next.delete(id));
      else             ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const bulkAction = async (sectionResults, action /* 'approve' | 'reject' */) => {
    const ids = sectionResults.map((e) => e.id).filter((id) => selectedIds.has(id));
    if (!ids.length) return;
    setBulkActing(true);
    try {
      const fn = action === 'approve' ? approveQueueEntry : rejectQueueEntry;
      const results = await Promise.allSettled(ids.map((id) => fn(id, { reviewer_notes: '' })));
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      setSnack({
        severity: failed === 0 ? (action === 'approve' ? 'success' : 'info') : 'warning',
        message: failed === 0
          ? `${action === 'approve' ? 'Approved' : 'Rejected'} ${ok} ${ok === 1 ? 'company' : 'companies'}.`
          : `${action === 'approve' ? 'Approved' : 'Rejected'} ${ok}; ${failed} failed.`,
      });
      // Drop the acted IDs from view
      setSections((prev) =>
        prev
          .map((s) => ({
            ...s,
            results: s.results.filter((e) => !ids.includes(e.id)),
            total_pending: Math.max(0, s.total_pending - ids.filter((id) => s.results.find((e) => e.id === id)).length),
          }))
          .filter((s) => s.results.length > 0 || s.total_pending > 0)
      );
      setSelectedIds(new Set());
    } finally { setBulkActing(false); }
  };

  // Header counter is by **company**, not contact — admins/BAs think in
  // companies. Dedupe by entry.company (id) or fall back to company_name
  // when the FK isn't surfaced. Sections are per-campaign so a company
  // queued in two campaigns counts as two (which is correct — they need
  // separate review under each campaign).
  const totalCompanies = sections.reduce((acc, s) => {
    const seen = new Set();
    for (const e of (s.results || [])) {
      const key = e.company != null ? `c:${e.company}` : `n:${e.company_name || ''}`;
      seen.add(key);
    }
    return acc + seen.size;
  }, 0);
  const totalContacts = sections.reduce((acc, s) => acc + (s.results?.length || 0), 0);
  // Kept for any existing code that still reads totalToday — same meaning
  // as before (contact count). The header below uses totalCompanies.
  const totalToday = totalContacts;

  return (
    <Page title="Daily Review | CRM">
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 24px)', bgcolor: 'background.default' }}>

        {/* Header */}
        <Box sx={{
          px: 2.5, py: 1.25, flexShrink: 0,
          borderBottom: 1, borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <Button size="small" startIcon={<Icon icon={arrowBackFill} />}
                  onClick={() => navigate('/my-outreach')}>
            My Outreach
          </Button>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Daily Review
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {loading
                ? 'Loading…'
                : totalCompanies === 0
                  ? 'Nothing waiting for you right now.'
                  : `${totalCompanies} compan${totalCompanies === 1 ? 'y' : 'ies'} (${totalContacts} contact${totalContacts === 1 ? '' : 's'}) to review today across ${sections.length} campaign${sections.length === 1 ? '' : 's'}.`}
            </Typography>
          </Box>
          <Button size="small" onClick={load} disabled={loading}>Refresh</Button>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
          {loading && <LinearProgress />}

          {!loading && sections.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">All clear ✓</Typography>
              <Typography variant="caption" color="text.secondary">
                No companies are waiting for your review.
              </Typography>
            </Box>
          )}

          {sections.map((section) => {
            const ids = section.results.map((e) => e.id);
            const selectedInSection = ids.filter((id) => selectedIds.has(id));
            const allSelected   = ids.length > 0 && selectedInSection.length === ids.length;
            const someSelected  = selectedInSection.length > 0 && !allSelected;
            const isOpen        = expandedCampaigns.has(section.campaign_id);

            return (
              <Paper key={section.campaign_id} variant="outlined"
                sx={{ borderRadius: 1.5, mb: 2, overflow: 'hidden' }}>
                {/* Collapsible section header — click anywhere to toggle */}
                <Box
                  onClick={() => toggleSectionExpanded(section.campaign_id)}
                  sx={{
                    px: 2, py: 1.5, cursor: 'pointer',
                    bgcolor: isOpen ? 'background.neutral' : 'background.paper',
                    borderBottom: isOpen ? '1px solid' : 'none',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                    <Icon icon={isOpen ? arrowIosDownwardFill : arrowIosForwardFill} width={18} />
                    {/* Select-all — only shown when expanded so it doesn't conflict with row toggle */}
                    {isOpen && ids.length > 0 && (
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={() => toggleSectionSelectAll(section)}
                        onClick={(ev) => ev.stopPropagation()}
                        sx={{ p: 0 }}
                      />
                    )}
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {section.campaign_name}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${section.results.length} to review`}
                      color="warning"
                      sx={{ height: 20, fontWeight: 700, fontSize: '0.7rem' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      · {section.acted_today ?? 0} done today
                      {section.cap ? ` of ${section.cap}` : ''}
                      {section.total_pending > section.results.length
                        && ` · ${section.total_pending - section.results.length} more queued for tomorrow`}
                    </Typography>
                    {section.cap > 0 && section.remaining === 0 && (
                      <Chip size="small" label="Daily quota reached"
                        sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'info.lighter', color: 'info.dark' }} />
                    )}

                    <Box sx={{ flex: 1 }} />
                    {/* Bulk action buttons — visible whenever selection exists, even when collapsed */}
                    {selectedInSection.length > 0 && (
                      <Stack direction="row" spacing={1} onClick={(ev) => ev.stopPropagation()}>
                        <Button
                          size="small" variant="outlined" color="error"
                          startIcon={<Icon icon={closeCircleFill} width={14} />}
                          disabled={bulkActing}
                          onClick={() => {
                            // eslint-disable-next-line no-restricted-globals, no-alert
                            if (confirm(`Reject ${selectedInSection.length} compan${selectedInSection.length === 1 ? 'y' : 'ies'}?`)) {
                              bulkAction(section.results, 'reject');
                            }
                          }}
                        >
                          Reject {selectedInSection.length}
                        </Button>
                        <Button
                          size="small" variant="contained" color="success"
                          startIcon={bulkActing
                            ? <CircularProgress size={12} color="inherit" />
                            : <Icon icon={checkmarkCircle2Fill} width={14} />}
                          disabled={bulkActing}
                          onClick={() => {
                            // eslint-disable-next-line no-restricted-globals, no-alert
                            if (confirm(`Approve ${selectedInSection.length} compan${selectedInSection.length === 1 ? 'y' : 'ies'} as-is (no message edits)?`)) {
                              bulkAction(section.results, 'approve');
                            }
                          }}
                        >
                          Approve {selectedInSection.length}
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                </Box>

                {/* Collapsible body */}
                {isOpen && <Box sx={{ p: 2 }}>
                {section.results.length === 0 ? (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    All done for today. {section.total_pending > 0
                      ? `${section.total_pending} more queued for tomorrow.`
                      : 'No more entries left.'}
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {[
                      { b: 'Today',  hint: "this BA's daily review quota",  match: (e) => e.is_today_review !== false },
                      { b: 'Future', hint: 'queued for later days',         match: (e) => e.is_today_review === false },
                    ].map(({ b, hint, match }) => {
                      const subset = section.results.filter(match);
                      if (subset.length === 0) return null;
                      const bucketKey  = `${section.campaign_id}:bucket:${b}`;
                      const bucketOpen = expandedCompanies.has(bucketKey);
                      return (
                      <Box key={bucketKey} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                        <Stack direction="row" alignItems="center" spacing={1}
                               onClick={() => toggleCompanyExpanded(bucketKey)}
                               sx={{ px: 1.5, py: 1, cursor: 'pointer', bgcolor: bucketOpen ? 'action.hover' : 'grey.100', '&:hover': { bgcolor: 'action.hover' } }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {bucketOpen ? '▾' : '▸'} {b} ({subset.length})
                          </Typography>
                          <Typography variant="caption" color="text.secondary">· {hint}</Typography>
                        </Stack>
                        {bucketOpen && (
                        <Stack spacing={1} sx={{ p: 1 }}>
                    {groupByCompany(subset).map((group) => {
                      const groupKey      = `${section.campaign_id}:${b}:${group.key}`;
                      const groupIds      = group.contacts.map((c) => c.id);
                      const selectedHere  = groupIds.filter((id) => selectedIds.has(id));
                      const allHereSel    = groupIds.length > 0 && selectedHere.length === groupIds.length;
                      const someHereSel   = selectedHere.length > 0 && !allHereSel;
                      // Every group (single OR multi contact) is collapsible —
                      // uniform UI so the queue looks consistent regardless of
                      // contact count.
                      const open          = expandedCompanies.has(groupKey);
                      const verifiedCount = group.contacts.filter(
                        (c) => c?.employment_verification?.confidence === 'verified'
                      ).length;

                      // Group-level checkbox toggles ALL contacts in the company.
                      const toggleGroup = () => {
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (allHereSel) groupIds.forEach((id) => next.delete(id));
                          else            groupIds.forEach((id) => next.add(id));
                          return next;
                        });
                      };

                      return (
                        <Box
                          key={groupKey}
                          sx={{
                            border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                            overflow: 'hidden', bgcolor: 'background.paper',
                          }}
                        >
                          {/* Company header — same layout for 1 contact or N */}
                          <Stack
                            direction="row" alignItems="center" spacing={1.25}
                            onClick={() => toggleCompanyExpanded(groupKey)}
                            sx={{
                              px: 1.5, py: 1.25,
                              cursor: 'pointer',
                              bgcolor: open ? 'action.hover' : 'transparent',
                              borderBottom: open ? '1px solid' : 'none',
                              borderColor: 'divider',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <Box sx={{ width: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Icon
                                icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
                                width={14}
                              />
                            </Box>

                            <Checkbox
                              size="small"
                              checked={allHereSel}
                              indeterminate={someHereSel}
                              onChange={toggleGroup}
                              onClick={(ev) => ev.stopPropagation()}
                              sx={{ p: 0 }}
                            />

                            <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
                              {group.companyName}
                            </Typography>

                            <Chip
                              size="small"
                              label={`${group.contacts.length} contact${group.contacts.length === 1 ? '' : 's'}`}
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                            {verifiedCount > 0 && (
                              <Chip
                                size="small"
                                label={`${verifiedCount} verified`}
                                color="success"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Stack>

                          {/* Contact rows — same layout whether 1 or N contacts */}
                          {open && (
                            <Stack spacing={0} sx={{ pl: 5, pr: 1.5, py: 0.5 }} divider={<Box sx={{ borderBottom: '1px dashed', borderColor: 'divider' }} />}>
                              {group.contacts.map((entry) => {
                                const isSelected = selectedIds.has(entry.id);
                                return (
                                  <Stack
                                    key={entry.id}
                                    direction="row" alignItems="center" spacing={1.25}
                                    sx={{
                                      py: 0.75,
                                      bgcolor: isSelected ? (t) => t.palette.action.selected : 'transparent',
                                      borderRadius: 0.5,
                                      '&:hover': { bgcolor: 'action.hover' },
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={isSelected}
                                      onChange={() => toggleSelected(entry.id)}
                                      onClick={(ev) => ev.stopPropagation()}
                                      sx={{ p: 0 }}
                                    />
                                    <Box
                                      sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                                      onClick={() => setPreviewEntry(entry)}
                                    >
                                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                                          {entry.contact_name || '—'}
                                        </Typography>
                                        <VerificationChip entry={entry} />
                                        {entry.assigned_pm_name && (
                                          <Chip size="small" variant="outlined" color="info"
                                                label={`BA: ${entry.assigned_pm_name}`}
                                                sx={{ height: 18, fontSize: '0.62rem' }} />
                                        )}
                                      </Stack>
                                      <Typography variant="caption" color="text.secondary" noWrap>
                                        {entry.contact_title || '—'}
                                        {entry.contact_email ? ` · ${entry.contact_email}` : ''}
                                      </Typography>
                                    </Box>
                                    <Button
                                      size="small" variant="outlined"
                                      startIcon={<Icon icon={eyeFill} width={14} />}
                                      onClick={() => setPreviewEntry(entry)}
                                    >
                                      Review
                                    </Button>
                                  </Stack>
                                );
                              })}
                            </Stack>
                          )}
                        </Box>
                      );
                    })}
                        </Stack>
                        )}
                      </Box>
                      );
                    })}
                  </Stack>
                )}
                </Box>}
              </Paper>
            );
          })}
        </Box>

        <ReviewDialog
          entry={previewEntry}
          siblingEntries={(() => {
            if (!previewEntry) return [];
            // Find other queue entries for the same company (same campaign).
            const sec = sections.find((s) => s.campaign_id === previewEntry.campaign);
            if (!sec) return [];
            return sec.results.filter(
              (e) => e.id !== previewEntry.id && e.company === previewEntry.company
            );
          })()}
          open={!!previewEntry}
          onClose={() => setPreviewEntry(null)}
          onActed={handleActed}
          onSnack={(s) => setSnack(s)}
        />

        <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack(null)}>
          {snack && <Alert severity={snack.severity}>{snack.message}</Alert>}
        </Snackbar>
      </Box>
    </Page>
  );
};

export default MyReviewQueue;
